import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import KuikaButton, { KUIKA_BUTTON_ICON_SIZES, type KuikaButtonSize } from "../kuika-button/kuika-button";
import KuikaSvg from "../kuika-svg/kuika-svg";
import KuikaSwap from "../kuika-swap/kuika-swap";
import { copyTextToClipboard } from "./copy-text-to-clipboard";
import "./kuika-copy-button.scss";

/** How long the check glyph (and the "Copied" label) stays before rolling back to the copy state. */
export const KUIKA_COPY_RESET_MS: number = 1500;

/**
 * The control rungs (`sm` 24 / `md` 32 / `lg` 40) plus `xs`: a 16px icon-only square on the icon
 * rung (`--control-icon-md`), for a value copied from a dense row — a key in a table cell, an id in
 * a panel line — where a 24px button would stand taller than the text beside it.
 */
export type KuikaCopyButtonSize = KuikaButtonSize | "xs";

/**
 * Like `KuikaButton`, it takes the standard button attributes it does not name — a tooltip or a
 * popover anchors to it by handing its trigger props and a ref to the control it wraps.
 */
export interface IKuikaCopyButtonProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "value" | "type" | "color" | "children" | "onCopy"
> {
  /** The text to copy, or a lazily-called getter (e.g. reading a Monaco model at click time). */
  value: string | (() => string);
  variant?: "icon" | "text";
  /**
   * Default `sm`. `xs` is the compact 16px square: the sm glyph (14px), the `--radius-xs` corner,
   * and still the button's full `--hit-min` click area. Icon variant only — the text variant draws
   * `xs` as `sm`.
   */
  size?: KuikaCopyButtonSize;
  type?: "primary" | "secondary" | "tertiary" | "text";
  color?: "default" | "neutral" | "danger";
  label?: string;
  copiedLabel?: string;
  onCopy?: (succeeded: boolean) => void;
  disabled?: boolean;
  className?: string;
  "data-testid"?: string;
}

/**
 * A single, reusable "copy to clipboard" control: its glyph rolls from a copy icon to a check
 * mark (via `KuikaSwap`) when the copy succeeds, then rolls back after `KUIKA_COPY_RESET_MS`.
 *
 * This replaces the several hand-written "copied" `useState` + `setTimeout` pairs scattered
 * across the designer (e.g. `kuika-toast.tsx`) and the bare `navigator.clipboard.writeText`
 * call sites that never handle a refused or insecure-context clipboard. A later pass moves
 * those call sites onto `copyTextToClipboard` / `KuikaCopyButton` directly.
 *
 * Reduced motion: `KuikaSwap`'s own keyframes already collapse the roll to a crossfade under
 * `prefers-reduced-motion`, so there is nothing extra to do here.
 */
const KuikaCopyButton = React.forwardRef<HTMLButtonElement, IKuikaCopyButtonProps>(function KuikaCopyButton(
  {
    value,
    variant = "icon",
    size = "sm",
    type = "text",
    color = "neutral",
    label = "Copy",
    copiedLabel = "Copied",
    onCopy,
    disabled = false,
    className,
    "data-testid": dataTestId,
    ...rest
  }: IKuikaCopyButtonProps,
  ref: React.ForwardedRef<HTMLButtonElement>
): JSX.Element {
  const [copied, setCopied] = useState<boolean>(false);
  const resetTimerRef: React.MutableRefObject<number | null> = useRef<number | null>(null);
  const isMountedRef: React.MutableRefObject<boolean> = useRef<boolean>(true);

  useEffect((): (() => void) => {
    isMountedRef.current = true;
    return (): void => {
      isMountedRef.current = false;
      if (resetTimerRef.current !== null) {
        window.clearTimeout(resetTimerRef.current);
        resetTimerRef.current = null;
      }
    };
  }, []);

  const handleClick = async (): Promise<void> => {
    const text: string = typeof value === "function" ? value() : value;
    const succeeded: boolean = await copyTextToClipboard(text);
    onCopy?.(succeeded);

    if (!succeeded || !isMountedRef.current) {
      return;
    }

    setCopied(true);

    if (resetTimerRef.current !== null) {
      window.clearTimeout(resetTimerRef.current);
    }
    resetTimerRef.current = window.setTimeout((): void => {
      resetTimerRef.current = null;
      if (isMountedRef.current) {
        setCopied(false);
      }
    }, KUIKA_COPY_RESET_MS);
  };

  // `xs` restates the sm rung's square at the icon rung's size (kuika-copy-button.scss).
  const buttonSize: KuikaButtonSize = size === "xs" ? "sm" : size;
  const isCompact: boolean = variant === "icon" && size === "xs";
  const iconSize: number = KUIKA_BUTTON_ICON_SIZES[variant === "icon" ? "sm" : buttonSize];
  const contentKey: "copy" | "copied" = copied ? "copied" : "copy";
  const currentLabel: string = copied ? copiedLabel : label;
  const rootClassName: string = classNames(
    "kuika-copy-button",
    { "kuika-copy-button--copied": copied, "kuika-copy-button--xs": isCompact },
    className
  );

  const glyph: JSX.Element = (
    <KuikaSvg name={copied ? "check" : "copy"} width={iconSize} height={iconSize} aria-hidden="true" />
  );

  if (variant === "icon") {
    return (
      <KuikaButton
        {...rest}
        ref={ref}
        type={type}
        color={color}
        size={buttonSize}
        isIcon
        disabled={disabled}
        onClick={handleClick}
        className={rootClassName}
        aria-label={currentLabel}
        data-testid={dataTestId}
      >
        <KuikaSwap contentKey={contentKey} variant="roll">
          {glyph}
        </KuikaSwap>
      </KuikaButton>
    );
  }

  return (
    <KuikaButton
      {...rest}
      ref={ref}
      type={type}
      color={color}
      size={buttonSize}
      disabled={disabled}
      onClick={handleClick}
      className={rootClassName}
      data-testid={dataTestId}
    >
      <span className="kuika-copy-button__live" aria-live="polite">
        <KuikaSwap contentKey={contentKey} variant="roll" leaving="overlay">
          <span className={classNames("kuika-copy-button__body", `kuika-copy-button__body--${buttonSize}`)}>
            {glyph}
            <span>{currentLabel}</span>
          </span>
        </KuikaSwap>
      </span>
    </KuikaButton>
  );
});

export default KuikaCopyButton;
